import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Heart, Plus, Search, AlertTriangle, ShieldCheck, Activity, Sparkles,
  ArrowRight, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const INJURY_TYPES = ['Sprain', 'Strain', 'Fracture', 'Concussion', 'Bruise', 'Cut', 'Dislocation', 'Tendinitis', 'Other'];
const BODY_PARTS = ['Head', 'Neck', 'Shoulder', 'Elbow', 'Wrist', 'Hand', 'Chest', 'Back', 'Hip', 'Knee', 'Ankle', 'Foot', 'Other'];
const SEVERITIES = ['minor', 'moderate', 'severe'];
const STATUS_FLOW = {
  reported: ['active', 'cleared'],
  active: ['recovering', 'cleared'],
  recovering: ['cleared', 'active'],
  cleared: ['reinjured'],
  reinjured: ['active', 'recovering'],
};

const statusMeta = {
  reported: { color: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Reported' },
  active: { color: 'bg-rose-100 text-rose-800 border-rose-300', label: 'Active' },
  recovering: { color: 'bg-blue-100 text-blue-800 border-blue-300', label: 'Recovering' },
  cleared: { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Cleared' },
  reinjured: { color: 'bg-rose-200 text-rose-900 border-rose-400', label: 'Reinjured' },
};
const severityMeta = {
  minor: { color: 'bg-amber-50 text-amber-700 border-amber-200' },
  moderate: { color: 'bg-orange-50 text-orange-700 border-orange-300' },
  severe: { color: 'bg-rose-50 text-rose-700 border-rose-300' },
};

const emptyForm = () => ({
  student_id: '',
  injury_type: 'Sprain',
  body_part: 'Knee',
  severity: 'minor',
  injury_date: new Date().toISOString().slice(0, 10),
  expected_recovery_date: '',
  description: '',
  recovery_protocols: '',
});

export default function InjuryTracker() {
  const { user } = useAuth();
  const canEdit = ['academy_owner', 'coach', 'employee', 'super_admin'].includes(user?.role);

  const [injuries, setInjuries] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', search: '' });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [lifecycleTarget, setLifecycleTarget] = useState(null);
  const [lifecycleNote, setLifecycleNote] = useState('');
  const [lifecycleSubmitting, setLifecycleSubmitting] = useState(false);

  const fetchInjuries = useCallback(async () => {
    try {
      const params = {};
      if (filters.status !== 'all') {
        if (filters.status === 'active_only') params.active_only = true;
        else params.status = filters.status;
      }
      const res = await api.get('/injuries', { params });
      setInjuries(res.data || []);
    } catch {
      toast.error('Failed to load injuries');
    } finally {
      setLoading(false);
    }
  }, [filters.status]);

  useEffect(() => { fetchInjuries(); }, [fetchInjuries]);
  useEffect(() => {
    api.get('/students', { params: { status: 'active' } }).then(r => setStudents(r.data || [])).catch(() => setStudents([]));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (inj) => {
    setEditing(inj);
    setForm({
      student_id: inj.student_id,
      injury_type: inj.injury_type || 'Sprain',
      body_part: inj.body_part || 'Knee',
      severity: inj.severity || 'minor',
      injury_date: inj.injury_date || '',
      expected_recovery_date: inj.expected_recovery_date || '',
      description: inj.description || '',
      recovery_protocols: inj.recovery_protocols || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!editing && !form.student_id) { toast.error('Pick a student'); return; }
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        delete payload.student_id;
        await api.put(`/injuries/${editing.id}`, payload);
        toast.success('Injury record updated');
      } else {
        await api.post('/injuries', form);
        toast.success('Injury logged — guardian will be notified');
      }
      setShowForm(false);
      fetchInjuries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleLifecycleConfirm = async () => {
    if (!lifecycleTarget) return;
    setLifecycleSubmitting(true);
    try {
      await api.post(`/injuries/${lifecycleTarget.injury.id}/lifecycle`, {
        to_status: lifecycleTarget.to,
        note: lifecycleNote,
      });
      toast.success(`Status updated to ${lifecycleTarget.to}`);
      setLifecycleTarget(null);
      setLifecycleNote('');
      fetchInjuries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to transition');
    } finally {
      setLifecycleSubmitting(false);
    }
  };

  const filteredInjuries = injuries.filter((i) => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const hay = `${i.student_name || ''} ${i.injury_type || ''} ${i.body_part || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const stats = {
    total: injuries.length,
    active: injuries.filter(i => ['reported', 'active', 'reinjured'].includes(i.status)).length,
    recovering: injuries.filter(i => i.status === 'recovering').length,
    cleared: injuries.filter(i => i.status === 'cleared').length,
  };

  if (loading) return (
    <div className="space-y-4" data-testid="injuries-loading">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      <Skeleton className="h-96 rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="injury-tracker-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <Heart className="h-6 w-6 text-rose-600" /> Injury Tracker
          </h1>
          <p className="text-sm text-muted-foreground">Field eligibility safety overrides · {injuries.length} record{injuries.length !== 1 ? 's' : ''}</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} data-testid="injury-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Log Injury
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/20 dark:to-pink-950/20 border-rose-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><AlertTriangle className="h-4 w-4 text-rose-600" /><Badge variant="outline" className="text-[9px]">Blocking</Badge></div>
            <p className="text-2xl font-display font-bold tabular-nums text-rose-700" data-testid="injuries-stat-active">{stats.active}</p>
            <p className="text-xs text-rose-700">Active / Reported</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><Activity className="h-4 w-4 text-blue-600" /><Badge variant="outline" className="text-[9px]">Limited</Badge></div>
            <p className="text-2xl font-display font-bold tabular-nums text-blue-700" data-testid="injuries-stat-recovering">{stats.recovering}</p>
            <p className="text-xs text-blue-700">In Recovery</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><ShieldCheck className="h-4 w-4 text-emerald-600" /><Badge variant="outline" className="text-[9px]">Returned</Badge></div>
            <p className="text-2xl font-display font-bold tabular-nums text-emerald-700" data-testid="injuries-stat-cleared">{stats.cleared}</p>
            <p className="text-xs text-emerald-700">Cleared</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-slate-50 to-zinc-50 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><Heart className="h-4 w-4 text-slate-600" /><Badge variant="outline" className="text-[9px]">All time</Badge></div>
            <p className="text-2xl font-display font-bold tabular-nums text-slate-700" data-testid="injuries-stat-total">{stats.total}</p>
            <p className="text-xs text-slate-700">Total Records</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Tabs value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <TabsList>
            <TabsTrigger value="all" data-testid="inj-tab-all">All</TabsTrigger>
            <TabsTrigger value="active_only" data-testid="inj-tab-active">Active</TabsTrigger>
            <TabsTrigger value="recovering" data-testid="inj-tab-recovering">Recovering</TabsTrigger>
            <TabsTrigger value="cleared" data-testid="inj-tab-cleared">Cleared</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-48 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search by name, injury type, body part…"
            className="pl-9 h-9 text-xs"
            data-testid="inj-search"
          />
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredInjuries.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground" data-testid="injuries-empty">
              <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>No injuries {filters.status !== 'all' ? 'matching this filter' : 'logged yet'}.</p>
              <p className="text-xs mt-1">When a student gets hurt, log it here to set field eligibility overrides.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Injury</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="whitespace-nowrap">Injury Date</TableHead>
                  <TableHead className="whitespace-nowrap">Expected Recovery</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInjuries.map((i) => {
                  const sm = statusMeta[i.status] || statusMeta.active;
                  const sev = severityMeta[i.severity] || severityMeta.minor;
                  const nextStates = STATUS_FLOW[i.status] || [];
                  return (
                    <TableRow key={i.id} data-testid={`injury-row-${i.id}`}>
                      <TableCell className="font-medium text-sm">{i.student_name || '—'}</TableCell>
                      <TableCell className="text-sm">
                        <div className="flex flex-col">
                          <span className="capitalize">{i.injury_type}</span>
                          <span className="text-[11px] text-muted-foreground">{i.body_part}</span>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className={`text-[10px] capitalize ${sev.color}`}>{i.severity}</Badge></TableCell>
                      <TableCell><Badge variant="outline" className={`text-[10px] ${sm.color}`} data-testid={`injury-status-${i.id}`}>{sm.label}</Badge></TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{i.injury_date || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{i.expected_recovery_date || '—'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 flex-wrap">
                          {canEdit && nextStates.map((next) => (
                            <Button
                              key={next}
                              size="sm"
                              variant="outline"
                              className="h-7 text-[10px] px-2 capitalize"
                              onClick={() => { setLifecycleTarget({ injury: i, to: next }); setLifecycleNote(''); }}
                              data-testid={`injury-transition-${i.id}-${next}`}
                            >
                              {next === 'cleared' ? <CheckCircle2 className="h-3 w-3 mr-0.5 text-emerald-600" /> : <ArrowRight className="h-3 w-3 mr-0.5" />}
                              {next}
                            </Button>
                          ))}
                          {canEdit && (
                            <Button size="sm" variant="ghost" className="h-7 text-[10px] px-2" onClick={() => openEdit(i)} data-testid={`injury-edit-${i.id}`}>Edit</Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={(v) => { setShowForm(v); if (!v) { setEditing(null); setForm(emptyForm()); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="injury-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Injury Record' : 'Log New Injury'}</DialogTitle>
            <DialogDescription>{editing ? 'Update details. Lifecycle status changes via the row buttons.' : 'Logging an injury triggers WhatsApp + Email notifications to the guardian.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div className="space-y-1.5">
                <Label className="text-xs">Student *</Label>
                <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                  <SelectTrigger data-testid="injury-form-student"><SelectValue placeholder="Pick a student…" /></SelectTrigger>
                  <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}{s.sport ? ` · ${s.sport}` : ''}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Injury Type *</Label>
                <Select value={form.injury_type} onValueChange={(v) => setForm({ ...form, injury_type: v })}>
                  <SelectTrigger data-testid="injury-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{INJURY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Body Part *</Label>
                <Select value={form.body_part} onValueChange={(v) => setForm({ ...form, body_part: v })}>
                  <SelectTrigger data-testid="injury-form-body-part"><SelectValue /></SelectTrigger>
                  <SelectContent>{BODY_PARTS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Severity *</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="injury-form-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEVERITIES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Injury Date</Label>
                <Input type="date" value={form.injury_date} onChange={(e) => setForm({ ...form, injury_date: e.target.value })} data-testid="injury-form-date" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Expected Recovery Date</Label>
              <Input type="date" value={form.expected_recovery_date} onChange={(e) => setForm({ ...form, expected_recovery_date: e.target.value })} data-testid="injury-form-recovery-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What happened, how it was assessed…" rows={2} data-testid="injury-form-description" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Recovery Protocol</Label>
              <Textarea value={form.recovery_protocols} onChange={(e) => setForm({ ...form, recovery_protocols: e.target.value })} placeholder="RICE, physio plan, return-to-play criteria…" rows={2} data-testid="injury-form-recovery-protocol" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} data-testid="injury-form-cancel">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="injury-form-save">{saving ? 'Saving…' : (editing ? 'Update' : 'Log Injury')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lifecycle confirm dialog */}
      <Dialog open={!!lifecycleTarget} onOpenChange={(v) => { if (!v) { setLifecycleTarget(null); setLifecycleNote(''); } }}>
        <DialogContent className="max-w-md" data-testid="injury-lifecycle-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">{lifecycleTarget?.injury?.student_name}: move to {lifecycleTarget?.to}</DialogTitle>
            <DialogDescription>
              {lifecycleTarget?.to === 'cleared' && 'Confirming Cleared marks the injury as fully recovered. Field eligibility returns to Eligible. Guardian will be notified.'}
              {lifecycleTarget?.to === 'recovering' && 'Moving to Recovering allows limited duty training. The student is still on restricted field eligibility.'}
              {lifecycleTarget?.to === 'active' && 'Mark as Active when the injury is confirmed and being managed. Field eligibility stays blocked.'}
              {lifecycleTarget?.to === 'reinjured' && 'Mark as Reinjured if the student suffered a setback. Lifecycle re-engages from the active phase.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Note (optional)</Label>
            <Textarea
              value={lifecycleNote}
              onChange={(e) => setLifecycleNote(e.target.value)}
              placeholder="e.g. Cleared by sports physio after fitness test"
              rows={2}
              data-testid="injury-lifecycle-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setLifecycleTarget(null); setLifecycleNote(''); }}>Cancel</Button>
            <Button onClick={handleLifecycleConfirm} disabled={lifecycleSubmitting} data-testid="injury-lifecycle-confirm">{lifecycleSubmitting ? 'Updating…' : `Move to ${lifecycleTarget?.to}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
